import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdeas } from '@/hooks/useIdeas';
import { calculateDaysSinceWatered, getTimeCue } from '@/lib/time-utils';
import { PageShell, Section } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { GrowthIcon } from '@/components/GrowthIcon';
import { Droplets, Target, AlertCircle, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Idea } from '@/lib/types';
import { formatDistanceToNow, subDays, isAfter, format } from 'date-fns';

export default function Dashboard() {
  const { ideas, loading } = useIdeas();
  const navigate = useNavigate();

  // Calculate required actions
  const requiredNext = useMemo(() => {
    const actions: Array<{
      type: 'water_focus' | 'revive' | 'set_focus';
      idea?: Idea;
      message: string;
      priority: number;
    }> = [];

    const focusIdea = ideas.find(i => i.is_focus && i.status === 'active');
    const activeIdeas = ideas.filter(i => i.status === 'active');

    // 1. No focus idea set
    if (!focusIdea && activeIdeas.length > 0) {
      actions.push({
        type: 'set_focus',
        message: 'Choose a focus idea to commit to.',
        priority: 1,
      });
    }

    // 2. Focus idea needs watering (2+ days)
    if (focusIdea) {
      const days = calculateDaysSinceWatered(focusIdea);
      if (days >= 2) {
        actions.push({
          type: 'water_focus',
          idea: focusIdea,
          message: `"${focusIdea.title || 'Your focus idea'}" has been quiet for ${days} days.`,
          priority: 2,
        });
      }
    }

    // 3. Near-withered ideas (5-6 days)
    const nearWithered = ideas.filter(i => {
      if (i.status !== 'active' || i.growth_stage === 'tree') return false;
      const days = calculateDaysSinceWatered(i);
      return days >= 5 && days < 7;
    });

    nearWithered.forEach(idea => {
      actions.push({
        type: 'revive',
        idea,
        message: `"${idea.title || 'Untitled'}" is close to withering.`,
        priority: 3,
      });
    });

    return actions.sort((a, b) => a.priority - b.priority);
  }, [ideas]);

  // Build timeline data for last 30 days
  const timeline = useMemo(() => {
    const thirtyDaysAgo = subDays(new Date(), 30);
    
    // Collect all activity events
    const events: Array<{
      date: Date;
      type: 'planted' | 'watered' | 'withered' | 'became_tree';
      idea: Idea;
    }> = [];

    ideas.forEach(idea => {
      const createdAt = new Date(idea.created_at);
      if (isAfter(createdAt, thirtyDaysAgo)) {
        events.push({ date: createdAt, type: 'planted', idea });
      }

      if (idea.last_watered_at) {
        const wateredAt = new Date(idea.last_watered_at);
        if (isAfter(wateredAt, thirtyDaysAgo) && idea.last_watered_at !== idea.created_at) {
          events.push({ date: wateredAt, type: 'watered', idea });
        }
      }

      if (idea.growth_stage === 'tree') {
        // Approximate when it became a tree (most recent activity)
        const becameTree = new Date(idea.last_watered_at || idea.updated_at);
        if (isAfter(becameTree, thirtyDaysAgo)) {
          events.push({ date: becameTree, type: 'became_tree', idea });
        }
      }
    });

    // Sort by date descending
    return events.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 20);
  }, [ideas]);

  // Stats
  const stats = useMemo(() => {
    const activeCount = ideas.filter(i => i.status === 'active').length;
    const treeCount = ideas.filter(i => i.growth_stage === 'tree').length;
    const witheredCount = ideas.filter(i => i.status === 'withered').length;
    const focusIdea = ideas.find(i => i.is_focus && i.status === 'active');
    
    return { activeCount, treeCount, witheredCount, focusIdea };
  }, [ideas]);

  if (loading) {
    return (
      <PageShell narrow>
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3 mt-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64 mt-6" />
      </PageShell>
    );
  }

  return (
    <PageShell narrow>
      {/* Header */}
      <header>
        <h1 className="text-xl font-medium text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground/70 mt-1">
          An audit of your thinking.
        </p>
      </header>

      {/* Stats Overview */}
      <Section>
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Active</p>
              <p className="text-2xl font-medium text-foreground mt-1">{stats.activeCount}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Trees</p>
              <p className="text-2xl font-medium text-primary mt-1">{stats.treeCount}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Withered</p>
              <p className="text-2xl font-medium text-muted-foreground/50 mt-1">{stats.witheredCount}</p>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* Required Next */}
      {requiredNext.length > 0 && (
        <Section title="Required Next">
          <div className="space-y-2">
            {requiredNext.map((action, index) => (
              <Card 
                key={index} 
                className={cn(
                  "cursor-pointer hover:bg-muted/30 transition-colors",
                  action.type === 'revive' && "border-amber-500/30"
                )}
                onClick={() => {
                  if (action.idea) {
                    navigate('/app', { state: { openIdeaId: action.idea.id } });
                  } else {
                    navigate('/app');
                  }
                }}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={cn(
                    "h-8 w-8 rounded-full flex items-center justify-center",
                    action.type === 'water_focus' && "bg-primary/10 text-primary",
                    action.type === 'revive' && "bg-amber-500/10 text-amber-600",
                    action.type === 'set_focus' && "bg-muted text-muted-foreground"
                  )}>
                    {action.type === 'water_focus' && <Droplets className="h-4 w-4" />}
                    {action.type === 'revive' && <AlertCircle className="h-4 w-4" />}
                    {action.type === 'set_focus' && <Target className="h-4 w-4" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{action.message}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>
      )}

      {/* Timeline */}
      <Section title="Recent Activity">
        {timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground/50 text-center py-8">
            No recent activity.
          </p>
        ) : (
          <div className="space-y-1">
            {timeline.map((event, index) => (
              <div 
                key={`${event.idea.id}-${event.type}-${index}`}
                className="flex items-center gap-4 py-2 px-3 rounded-lg hover:bg-muted/20 transition-colors"
              >
                <div className="w-20 text-xs text-muted-foreground/60 flex-shrink-0">
                  {format(event.date, 'MMM d')}
                </div>
                <GrowthIcon stage={event.idea.growth_stage} size={16} className="flex-shrink-0 opacity-60" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-foreground truncate block">
                    {event.idea.title || 'Untitled'}
                  </span>
                </div>
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full",
                  event.type === 'planted' && "bg-primary/10 text-primary",
                  event.type === 'watered' && "bg-secondary/30 text-secondary-foreground",
                  event.type === 'became_tree' && "bg-primary/20 text-primary",
                  event.type === 'withered' && "bg-muted text-muted-foreground"
                )}>
                  {event.type === 'planted' && 'Planted'}
                  {event.type === 'watered' && 'Watered'}
                  {event.type === 'became_tree' && 'Tree'}
                  {event.type === 'withered' && 'Withered'}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </PageShell>
  );
}
